<template>
  <li class="org-branch" :class="{ 'org-branch--root': isRoot }">
    <div class="org-branch__node">
      <org-chart-person-card
        ref="card"
        :person="branch.person"
        :area-label="areaLabel(branch.person.area)"
        :highlighted="branch.person.personId === highlightPersonId"
        @click="$emit('select-person', branch.person)"
      />
      <v-btn
        v-if="branch.children.length"
        x-small
        text
        class="org-branch__toggle"
        :aria-label="isExpanded(branch) ? 'Ocultar equipo' : 'Ver equipo'"
        @click="$emit('toggle', branch.person.personId)"
      >
        <v-icon x-small left>{{ isExpanded(branch) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        {{ branch.children.length }}
      </v-btn>
    </div>

    <ul v-if="branch.children.length && isExpanded(branch)" class="org-branch__children">
      <org-chart-branch
        v-for="child in branch.children"
        :key="child.person.personId"
        :branch="child"
        :areas="areas"
        :highlight-person-id="highlightPersonId"
        :is-expanded="isExpanded"
        @select-person="$emit('select-person', $event)"
        @toggle="$emit('toggle', $event)"
      />
    </ul>
  </li>
</template>

<script>
import OrgChartPersonCard from './OrgChartPersonCard.vue';

// Rama recursiva de OrgChartTree: una caja y, si está abierta, sus reportes.
export default {
  name: 'OrgChartBranch',
  components: { OrgChartPersonCard },
  props: {
    branch: { type: Object, required: true },
    areas: { type: Object, default: () => ({}) },
    highlightPersonId: { type: [Number, String], default: null },
    // Función (branch) => boolean que decide si la rama está abierta. Vive en OrgChartTree.
    isExpanded: { type: Function, required: true },
    isRoot: { type: Boolean, default: false }
  },
  watch: {
    highlightPersonId: {
      immediate: true,
      handler(id) {
        if (id === null || id !== this.branch.person.personId) return;
        this.$nextTick(() => {
          const el = this.$refs.card && this.$refs.card.$el;
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        });
      }
    }
  },
  methods: {
    areaLabel(area) {
      return (area && this.areas[area]) || '';
    }
  }
};
</script>

<style scoped>
/* Conectores del organigrama: cada <li> dibuja la mitad de la línea horizontal
   que lo une con sus hermanos y la línea vertical que baja a su caja. */
.org-branch {
  list-style: none;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 6px 0;
}

.org-branch::before,
.org-branch::after {
  content: '';
  position: absolute;
  top: 0;
  right: 50%;
  width: 50%;
  height: 20px;
  border-top: 1px solid var(--pw-border-strong);
}

.org-branch::after {
  right: auto;
  left: 50%;
  border-left: 1px solid var(--pw-border-strong);
}

.org-branch:only-child::before,
.org-branch:only-child::after {
  border-top: 0 none;
}

.org-branch:only-child::before {
  display: none;
}

.org-branch:first-child::before,
.org-branch:last-child::after {
  border: 0 none;
}

.org-branch:last-child::before {
  border-right: 1px solid var(--pw-border-strong);
  border-radius: 0 6px 0 0;
}

.org-branch:first-child::after {
  border-radius: 6px 0 0 0;
}

.org-branch:only-child::after {
  border-radius: 0;
}

.org-branch--root {
  padding-top: 0;
}

.org-branch--root::before,
.org-branch--root::after {
  display: none;
}

.org-branch__node {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.org-branch__toggle {
  margin-top: 2px;
  color: var(--pw-text-muted);
}

.org-branch__children {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 20px 0 0;
  margin: 0;
}

.org-branch__children::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  height: 20px;
  border-left: 1px solid var(--pw-border-strong);
}
</style>
